import { Injectable } from '@angular/core';
import { BaseService } from './base-service';
import { HttpClient, HttpParams } from '@angular/common/http';
import { CoreStatusDTO } from '../model/core-status-dto';
import { NodeConfigurationDTO } from '../model/configuration/node-configuration-dto';
import { PageDTO } from '../model/page-dto';

import { Router } from '@angular/router';
import { DiagnosisDataDTO } from '../model/diagnosis/diagnosis-data-dto';
import { CalibrationDetectorDTO } from '../model/calibration-detector-dto';
import { NodeDTO } from '../model/nodes/node-dto';

@Injectable({
    providedIn: 'root'
})
export class NodeService extends BaseService {

    constructor(http: HttpClient, router: Router) {
        super(http, router)
    }

    getNodes(pageNumber: number, pageSize: number, order: string, validated?: boolean, activated?: boolean): Promise<PageDTO<NodeDTO>> {
        let parameters = new HttpParams()
            .set('pageNumber', pageNumber)
            .set('pageSize', pageSize)
            .set('order', order)

        if (validated !== undefined) {
            parameters = parameters.set('validated', validated)
        }
        if (activated !== undefined) {
            parameters = parameters.set('activated', activated)
        }
        let request = this.get<PageDTO<NodeDTO>>("nodes", parameters);
        return this.apiCall<PageDTO<NodeDTO>>(request)
    }

    update(node: NodeDTO): Promise<void> {
        const request = this.put<void>("nodes", node);
        return this.apiCall<void>(request);
    }

    deleteNode(id: number): Promise<void> {
        const request = this.delete<void>(`nodes/${id}`);
        return this.apiCall<void>(request);
    }

    getStatus(id: number): Promise<CoreStatusDTO> {
        const request = this.get<CoreStatusDTO>(`nodes/${id}`);
        return this.apiCall<CoreStatusDTO>(request);
    }

    validate(id: number): Promise<CoreStatusDTO> {
        const request = this.put<CoreStatusDTO>(`nodes/${id}/validate`)
        return this.apiCall<CoreStatusDTO>(request);
    }

    unvalidate(id: number): Promise<CoreStatusDTO> {
        const request = this.put<CoreStatusDTO>(`nodes/${id}/unvalidate`)
        return this.apiCall<CoreStatusDTO>(request);
    }

    getNodeConfiguration(id: number): Promise<NodeConfigurationDTO> {
        const request = this.get<NodeConfigurationDTO>(`nodes/${id}/configuration`);
        return this.apiCall<NodeConfigurationDTO>(request);
    }

    saveNodeConfiguration(id: number, configuration: NodeConfigurationDTO): Promise<void> {
        const request = this.put<void>(`nodes/${id}/configuration`, configuration);
        return this.apiCall<void>(request);
    }

    getNodeDiagnosisData(node: number, anomaly: CalibrationDetectorDTO): Promise<DiagnosisDataDTO[]> {
        let parameters = new HttpParams()
            .set('timestamps', anomaly.timestamp.toString())

        anomaly.features.split(',').forEach(feat => {
            parameters = parameters.append("features", feat);
        });

        let request = this.get<DiagnosisDataDTO[]>(`nodes/${node}/diagnosis`, parameters);
        return this.apiCall<DiagnosisDataDTO[]>(request)
    }

    startWorking(id: number): Promise<CoreStatusDTO> {
        const request = this.post<CoreStatusDTO>(`nodes/${id}/start/working`);
        return this.apiCall<CoreStatusDTO>(request);
    }

    stopWorking(id: number): Promise<CoreStatusDTO> {
        const request = this.post<CoreStatusDTO>(`nodes/${id}/stop`);
        return this.apiCall<CoreStatusDTO>(request);
    }

    startParserLearning(id: number, timeLimitHours: number, timeLimitHoursWithoutFeatures: number, variables: { [dataSource: string]: string[]; }): Promise<CoreStatusDTO> {
        let parameters = new HttpParams()
            .set('timeLimitHours', timeLimitHours)
            .set('timeLimitHoursWithoutFeatures', timeLimitHoursWithoutFeatures)

        const request = this.post<CoreStatusDTO>(`nodes/${id}/start/parser-learning`, variables, parameters);
        return this.apiCall<CoreStatusDTO>(request);
    }

    skipParserLearning(id: number): Promise<CoreStatusDTO> {
        const request = this.post<CoreStatusDTO>(`nodes/${id}/skip-parser-learning`);
        return this.apiCall<CoreStatusDTO>(request);
    }

    startDetectorLearning(id: number, timeLimitHours: number, uclVariationPercentage: number): Promise<CoreStatusDTO> {
        let parameters = new HttpParams()
            .set('timeLimitHours', timeLimitHours)
            .set('uclVariationPercentage', uclVariationPercentage)

        const request = this.post<CoreStatusDTO>(`nodes/${id}/start/detector-learning`, null, parameters);
        return this.apiCall<CoreStatusDTO>(request);
    }

    finish(id: number): Promise<CoreStatusDTO> {
        const request = this.post<CoreStatusDTO>(`nodes/${id}/finish`);
        return this.apiCall<CoreStatusDTO>(request);
    }

    cancel(id: number): Promise<CoreStatusDTO> {
        const request = this.post<CoreStatusDTO>(`nodes/${id}/cancel`);
        return this.apiCall<CoreStatusDTO>(request);
    }

    reset(id: number): Promise<CoreStatusDTO> {
        const request = this.post<CoreStatusDTO>(`nodes/${id}/reset`);
        return this.apiCall<CoreStatusDTO>(request);
    }
}
